/**
 * Organigrama de ubicaciones (padre → hijos, multinivel).
 *
 * Se usa en la ficha de ubicación para ver de un vistazo dónde está colgada
 * la sede actual y para crear hijas al vuelo (salas dentro de una planta,
 * plantas dentro de un edificio…). No guarda estado: recibe la lista plana de
 * ubicaciones y avisa al padre cuando se pulsa «+».
 */
import React from 'react';
import { PlusIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { typeIcon, typeLabel, buildLocationMap, locationRootId } from '../utils/locationTree';

export default function LocationOrgChart({ locations = [], currentId = null, onAdd, onRename }) {
  const map = buildLocationMap(locations);
  const hijas = new Map();
  locations.forEach((loc) => {
    const clave = loc.parent_id == null ? 'root' : String(loc.parent_id);
    if (!hijas.has(clave)) hijas.set(clave, []);
    hijas.get(clave).push(loc);
  });
  hijas.forEach((lista) => lista.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })));

  const childrenOf = (id) => hijas.get(id == null ? 'root' : String(id)) || [];

  // Contexto: la rama de la raíz de la ubicación actual (o el bosque completo).
  const actual = currentId != null ? map.get(String(currentId)) : null;
  const raizId = currentId != null ? locationRootId(map, currentId) : null;
  const raiz = raizId != null ? map.get(String(raizId)) : null;
  const madre = actual?.parent_id != null ? map.get(String(actual.parent_id)) : null;

  const renderNodo = (loc) => {
    const sub = childrenOf(loc.id);
    const actual = currentId != null && String(loc.id) === String(currentId);
    return (
      <li key={loc.id} className="sf2-orgnode">
        <div className={`sf2-orgrow${actual ? ' sf2-orgrow--current' : ''}${loc.is_active === false ? ' sf2-orgrow--off' : ''}`}>
          <span className="sf2-orgicon" aria-hidden="true">{typeIcon(loc.location_type)}</span>
          <span className="sf2-orgname">
            {loc.name}
            <small>
              {typeLabel(loc.location_type)}
              {loc.code ? ` · ${loc.code}` : ''}
              {sub.length > 0 ? ` · ${sub.length} dentro` : ''}
            </small>
          </span>
          {onRename && (
            <button
              type="button"
              className="sf2-orgadd"
              title={`Cambiar el nombre de ${loc.name}`}
              onClick={() => onRename(loc)}
            >
              <PencilSquareIcon />
            </button>
          )}
          {onAdd && (
            <button
              type="button"
              className="sf2-orgadd"
              title={`Añadir una ubicación dentro de ${loc.name}`}
              onClick={() => onAdd(loc)}
            >
              <PlusIcon />
            </button>
          )}
        </div>
        {sub.length > 0 && <ul className="sf2-orgchildren">{sub.map(renderNodo)}</ul>}
      </li>
    );
  };

  // Con ubicación actual: solo la rama de su raíz. Sin ella: todo el bosque.
  const raices = raiz ? [raiz] : childrenOf(null);

  if (raices.length === 0) {
    return <div className="sf2-empty">Todavía no hay ubicaciones creadas.</div>;
  }

  return (
    <>
      {raiz && (
        <div className="sf2-orgcaption">
          Rama de <b>{raiz.name}</b>
          {madre ? ` · ${actual.name} cuelga de ${madre.name}` : ''}
        </div>
      )}
      <ul className="sf2-orgtree">{raices.map(renderNodo)}</ul>
    </>
  );
}
